import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { formService } from '../../../services/formServices';
import './Modal.scss';

const JobApplyModal = ({ onClose, jobId, jobPosition = "Embedded Engineer" }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: '',
        resumeUrl: '',
        job: jobId || ''
    });

    const [resumeFile, setResumeFile] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [uploadError, setUploadError] = useState('');

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploadError('');
        const validTypes = [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.ms-word'
        ];
        const validExtensions = ['.pdf', '.doc', '.docx'];
        const maxSize = 5 * 1024 * 1024;

        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
        if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
            setUploadError('Please upload a PDF, DOC, or DOCX file');
            return;
        }

        if (file.size > maxSize) {
            setUploadError('File size must be less than 5MB');
            return;
        }

        setResumeFile(file);
        setUploadingFile(true);
        setUploadProgress(0);

        try {
            const response = await formService.uploadResume(file);
            console.log(response)

            if (response && response?.fileUrl) {
                setFormData(prev => ({
                    ...prev,
                    resumeUrl: response?.fileUrl
                }));
                setUploadProgress(100);
                setUploadError('');
            } else {
                throw new Error('No URL returned from upload');
            }
        } catch (error) {
            console.error('File upload error:', error);
            setUploadError(
                error.response?.data?.message ||
                error.message ||
                'Failed to upload resume. Please try again.'
            );
            setResumeFile(null);
            setFormData(prev => ({ ...prev, resumeUrl: '' }));
        } finally {
            setTimeout(() => {
                setUploadingFile(false);
                setUploadProgress(0);
            }, 500);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!jobId) {
            alert('Job ID is required');
            return;
        }

        if (!resumeFile || !formData.resumeUrl) {
            alert('Please upload your resume');
            return;
        }

        if (!formData.name || !formData.email || !formData.phone) {
            alert('Please fill in all required fields');
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            alert('Please enter a valid email address');
            return;
        }

        setIsSubmitting(true);

        try {
            const submissionData = {
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                message: formData.message,
                resumeUrl: formData.resumeUrl,
                job: jobId,
                jobRole: jobPosition
            };

            await formService.submitCareerApplication(submissionData);
            alert("Application submitted successfully!");

            setFormData({
                name: '',
                email: '',
                phone: '',
                message: '',
                resumeUrl: '',
                job: jobId
            });
            setResumeFile(null);
            setUploadError('');

            onClose();
        } catch (error) {
            console.error('Application submission error:', error);
            alert(error.response?.data?.message || "Failed to submit application. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleRemoveFile = () => {
        setResumeFile(null);
        setFormData(prev => ({ ...prev, resumeUrl: '' }));
        setUploadError('');
    };

    return (
        <div className="contact-modal">
            <div className="modal-grid">
                {/* Left Column */}
                <div className="modal-left order-mobile-2">
                    <motion.div
                        className="modal-header"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                    >
                        <span className="modal-label career-label">CAREER OPPORTUNITY</span>
                        <h2 className="modal-title">Join Our Team</h2>
                        <p className="modal-subtitle">
                            {jobPosition ? `Applying for: ${jobPosition}` : "We're excited to review your application!"}
                        </p>
                    </motion.div>

                    <motion.div
                        className="application-info glass-card"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                    >
                        <h4 className="info-title">Application Requirements</h4>
                        <ul className="requirement-list">
                            <li className="requirement-item">
                                <span className="check-icon">✓</span>
                                <span>Resume (PDF, DOC, DOCX) - Max 5MB</span>
                            </li>
                            <li className="requirement-item">
                                <span className="check-icon">✓</span>
                                <span>Valid email address</span>
                            </li>
                            <li className="requirement-item">
                                <span className="check-icon">✓</span>
                                <span>Phone number for contact</span>
                            </li>
                        </ul>

                        <div className="application-note"> <p>Questions? Email us at <strong>business@logiqlink.com</strong></p>
                        </div>
                    </motion.div>

                    {/* <motion.div
                        className="contact-hint"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                    >
                        <p>Questions? Email us at <strong>careers@logiqlink.com</strong></p>
                    </motion.div> */}
                </div>

                {/* Right Column - Form */}
                <motion.div
                    className="modal-right order-mobile-1"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                >
                    <div className="form-wrapper glass-card">
                        <h3 className="form-title">Apply Now</h3>
                        <form onSubmit={handleSubmit} className="contact-form">
                            <div className="form-row">
                                <div className="form-group">
                                    <input
                                        type="text"
                                        name="name"
                                        placeholder="Full Name *"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                        className="form-input"
                                        disabled={isSubmitting || uploadingFile}
                                    />
                                </div>

                                <div className="form-group">
                                    <input
                                        type="email"
                                        name="email"
                                        placeholder="Email Address *"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        required
                                        className="form-input"
                                        disabled={isSubmitting || uploadingFile}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <input
                                    type="tel"
                                    name="phone"
                                    placeholder="Phone Number *"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    required
                                    className="form-input"
                                    disabled={isSubmitting || uploadingFile}
                                />
                            </div>

                            <div className="form-group">
                                <label className="file-label">
                                    Upload Resume *
                                    <span className="file-hint">(PDF, DOC, DOCX - Max 5MB)</span>
                                </label>

                                <div className="file-upload-area">
                                    <input
                                        type="file"
                                        onChange={handleFileChange}
                                        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                        required
                                        className="file-input"
                                        disabled={isSubmitting || uploadingFile}
                                    />
                                    <div className="upload-placeholder">
                                        <span className="upload-icon">📄</span>
                                        <span className="upload-text">
                                            {uploadingFile ? 'Uploading...' : 'Click to browse or drag & drop'}
                                        </span>
                                    </div>
                                </div>

                                {uploadingFile && (
                                    <div className="upload-progress-wrapper">
                                        <div className="progress-info">
                                            <span>{resumeFile?.name}</span>
                                            <span>{uploadProgress}%</span>
                                        </div>
                                        <div className="progress-bar-container">
                                            <div
                                                className="progress-bar"
                                                style={{ width: `${uploadProgress}%` }}
                                            />
                                        </div>
                                    </div>
                                )}

                                {resumeFile && !uploadingFile && (
                                    <div className="file-preview">
                                        <div className="file-info">
                                            <span className="file-icon">📎</span>
                                            <div className="file-details">
                                                <span className="file-name">{resumeFile.name}</span>
                                                <span className="file-size">
                                                    {(resumeFile.size / 1024 / 1024).toFixed(2)} MB
                                                </span>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className="remove-file-btn"
                                            onClick={handleRemoveFile}
                                            disabled={isSubmitting}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                )}

                                {uploadError && (
                                    <div className="upload-error">
                                        ⚠️ {uploadError}
                                    </div>
                                )}
                            </div>

                            <div className="form-group">
                                <textarea
                                    name="message"
                                    placeholder="Additional Message (Optional)"
                                    rows="3"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    className="form-textarea"
                                    disabled={isSubmitting || uploadingFile}
                                />
                            </div>

                            <motion.button
                                type="submit"
                                className="submit-btn"
                                disabled={isSubmitting || uploadingFile || !resumeFile || !formData.resumeUrl}
                                whileHover={(!isSubmitting && !uploadingFile && resumeFile && formData.resumeUrl) ? { scale: 1.02 } : {}}
                                whileTap={(!isSubmitting && !uploadingFile && resumeFile && formData.resumeUrl) ? { scale: 0.98 } : {}}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="spinner"></span>
                                        Submitting...
                                    </>
                                ) : uploadingFile ? (
                                    <>
                                        <span className="spinner"></span>
                                        Uploading...
                                    </>
                                ) : 'Submit'}
                            </motion.button>
                        </form>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default JobApplyModal;